import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';
import { 
  Loader2, Check, X, AlertCircle, Package, DollarSign, Building, 
  Calendar, FileText, BarChart3, TrendingUp, TrendingDown, 
  ChevronDown, ChevronUp, Eye, Download, Trash2, Sun, Moon, ZoomIn, ZoomOut,
  RotateCw, Search, Printer, Save, Edit3, Copy, CheckCircle2, XCircle,
  AlertTriangle, Info, MoreHorizontal
} from 'lucide-react';

export default function InvoiceReview() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editedData, setEditedData] = useState(null);
  const [showConfidenceDetails, setShowConfidenceDetails] = useState(false);
  const [showOriginal, setShowOriginal] = useState(false);
  const [activeTab, setActiveTab] = useState('items');
  
  // Image viewer controls
  const [imageZoom, setImageZoom] = useState(100);
  const [imageBrightness, setImageBrightness] = useState(100);
  const [imageContrast, setImageContrast] = useState(100);
  const [imageRotation, setImageRotation] = useState(0);
  const [imageInverted, setImageInverted] = useState(false);
  
  // Search and filter
  const [searchTerm, setSearchTerm] = useState('');
  const [filterMatched, setFilterMatched] = useState('all');
  
  // Bulk selection
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  
  // Edit mode
  const [isEditing, setIsEditing] = useState(false);
  const [originalData, setOriginalData] = useState(null);

  useEffect(() => {
    if (id) {
      loadInvoice();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadInvoice = async () => {
    try {
      const response = await api.get(`/invoices/${id}`);
      setInvoice(response.data);
      setEditedData(response.data.extracted_data);
    } catch (error) {
      toast.error('Failed to load invoice');
      navigate('/invoices');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    setApproving(true);
    try {
      // First save any edits
      if (editedData && JSON.stringify(editedData) !== JSON.stringify(invoice.extracted_data)) {
        await api.put(`/invoices/${id}/review`, {
          extracted_data: editedData
        });
      }
      
      // Then approve
      await api.post(`/invoices/${id}/approve`);
      toast.success('Invoice approved and inventory updated!');
      navigate('/invoices');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve invoice');
    } finally {
      setApproving(false);
    }
  };

  const handleReject = async () => {
    const reason = prompt('Why are you rejecting this invoice?');
    if (!reason) return;
    
    setRejecting(true);
    try {
      await api.post(`/invoices/${id}/reject`, { reason });
      toast.success('Invoice rejected');
      navigate('/invoices');
    } catch (error) {
      toast.error('Failed to reject invoice');
    } finally {
      setRejecting(false);
    }
  };

  const updateLineItem = (index, field, value) => {
    const newItems = [...editedData.line_items];
    newItems[index] = { ...newItems[index], [field]: value };
    setEditedData({ ...editedData, line_items: newItems });
  };

  const removeLineItem = (index) => {
    const newItems = editedData.line_items.filter((_, i) => i !== index);
    setEditedData({ ...editedData, line_items: newItems });
    toast.success('Item removed');
  };

  const addLineItem = () => {
    const newItem = {
      item_name: '',
      quantity: 1,
      unit: 'each',
      unit_cost: 0,
      total_cost: 0,
      upc: '',
      sku: '',
      category: ''
    };
    setEditedData({
      ...editedData,
      line_items: [...(editedData.line_items || []), newItem]
    });
  };

  const calculateTotals = () => {
    const items = editedData?.line_items || [];
    const subtotal = items.reduce((sum, item) => {
      const qty = parseFloat(item.quantity) || 0;
      const cost = parseFloat(item.unit_cost) || 0;
      return sum + (qty * cost);
    }, 0);
    
    const tax = parseFloat(editedData?.tax) || 0;
    const total = subtotal + tax;
    
    return { subtotal, tax, total, itemCount: items.length };
  };

  const getConfidenceColor = (confidence) => {
    if (confidence >= 80) return 'bg-green-100 text-green-800 border-green-200';
    if (confidence >= 50) return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    return 'bg-red-100 text-red-800 border-red-200';
  };

  const getConfidenceIcon = (confidence) => {
    if (confidence >= 80) return <TrendingUp className="w-5 h-5" />;
    if (confidence >= 50) return <AlertCircle className="w-5 h-5" />;
    return <TrendingDown className="w-5 h-5" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!invoice || !editedData) {
    return (
      <div className="text-center p-8">
        <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <p>Invoice not found or extraction failed</p>
      </div>
    );
  }

  const totals = calculateTotals();
  const confidencePercent = Math.round((invoice.ai_confidence || 0));
  const confidenceBreakdown = invoice.confidence_breakdown || {};

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold">Review Invoice</h1>
          <p className="text-gray-600">Verify and edit extracted data before approving</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowOriginal(!showOriginal)}
            className="px-4 py-2 border rounded-lg hover:bg-gray-50 flex items-center"
          >
            <Eye className="w-4 h-4 mr-2" />
            {showOriginal ? 'Hide Original' : 'View Original'}
          </button>
          <button
            onClick={() => window.open(`/api/invoices/${id}/image`, '_blank')}
            className="px-4 py-2 border rounded-lg hover:bg-gray-50 flex items-center"
          >
            <Download className="w-4 h-4 mr-2" />
            Download
          </button>
        </div>
      </div>

      {/* Original Image Preview with Controls */}
      {showOriginal && (
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold">Original Invoice</h3>
            <div className="flex gap-2">
              {/* Image Controls */}
              <div className="flex items-center gap-2 bg-gray-100 rounded-lg px-3 py-1">
                <button 
                  onClick={() => setImageZoom(Math.max(50, imageZoom - 25))}
                  className="p-1 hover:bg-gray-200 rounded"
                  title="Zoom Out"
                >
                  <ZoomOut className="w-4 h-4" />
                </button>
                <span className="text-sm font-medium w-12 text-center">{imageZoom}%</span>
                <button 
                  onClick={() => setImageZoom(Math.min(200, imageZoom + 25))}
                  className="p-1 hover:bg-gray-200 rounded"
                  title="Zoom In"
                >
                  <ZoomIn className="w-4 h-4" />
                </button>
              </div>
              
              <div className="flex items-center gap-2 bg-gray-100 rounded-lg px-3 py-1">
                <button 
                  onClick={() => setImageBrightness(Math.max(50, imageBrightness - 20))}
                  className="p-1 hover:bg-gray-200 rounded"
                  title="Darker"
                >
                  <Moon className="w-4 h-4" />
                </button>
                <span className="text-sm font-medium w-12 text-center">{imageBrightness}%</span>
                <button 
                  onClick={() => setImageBrightness(Math.min(150, imageBrightness + 20))}
                  className="p-1 hover:bg-gray-200 rounded"
                  title="Brighter"
                >
                  <Sun className="w-4 h-4" />
                </button>
              </div>
              
              <button 
                onClick={() => setImageContrast(Math.min(200, imageContrast + 20))}
                className="flex items-center gap-1 px-3 py-1 bg-gray-100 rounded-lg hover:bg-gray-200"
                title="Increase Contrast"
              >
                <span className="text-sm font-medium">Contrast</span>
                <span className="text-xs">{imageContrast}%</span>
              </button>
              
              <button 
                onClick={() => setImageRotation((imageRotation + 90) % 360)}
                className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200"
                title="Rotate"
              >
                <RotateCw className="w-4 h-4" />
              </button>
              
              <button 
                onClick={() => setImageInverted(!imageInverted)}
                className={`p-2 rounded-lg ${imageInverted ? 'bg-blue-600 text-white' : 'bg-gray-100 hover:bg-gray-200'}`}
                title="Invert Colors"
              >
                <Sun className="w-4 h-4" />
              </button>
              
              <button 
                onClick={() => {
                  setImageZoom(100);
                  setImageBrightness(100);
                  setImageContrast(100);
                  setImageRotation(0);
                  setImageInverted(false);
                }}
                className="px-3 py-1 bg-gray-200 rounded-lg hover:bg-gray-300 text-sm"
              >
                Reset
              </button>
            </div>
          </div>
          
          <div className="overflow-auto max-h-[600px] border rounded bg-gray-50">
            <img 
              src={`/api/invoices/${id}/image`} 
              alt="Original invoice" 
              className="mx-auto transition-all duration-200"
              style={{
                transform: `scale(${imageZoom / 100}) rotate(${imageRotation}deg)`,
                filter: `brightness(${imageBrightness}%) contrast(${imageContrast}%) ${imageInverted ? 'invert(100%)' : ''}`,
                transformOrigin: 'center center'
              }}
            />
          </div>
          
          <p className="text-sm text-gray-500 mt-2">
            Tip: Use brightness and contrast controls to make text more readable. Invert colors for dark backgrounds.
          </p>
        </div>
      )}

      {/* AI Confidence Card */}
      <div className={`rounded-lg p-4 mb-6 border ${getConfidenceColor(confidencePercent)}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            {getConfidenceIcon(confidencePercent)}
            <div className="ml-3">
              <span className="font-semibold text-lg">
                AI Confidence: {confidencePercent}%
              </span>
              <p className="text-sm opacity-80">
                {confidencePercent >= 80 
                  ? 'High confidence - data looks accurate'
                  : confidencePercent >= 50
                  ? 'Medium confidence - please review carefully'
                  : 'Low confidence - please verify all data'}
              </p>
            </div>
          </div>
          <button
            onClick={() => setShowConfidenceDetails(!showConfidenceDetails)}
            className="flex items-center text-sm hover:opacity-70"
          >
            Details
            {showConfidenceDetails ? <ChevronUp className="w-4 h-4 ml-1" /> : <ChevronDown className="w-4 h-4 ml-1" />}
          </button>
        </div>
        
        {showConfidenceDetails && confidenceBreakdown && (
          <div className="mt-4 pt-4 border-t border-current border-opacity-20">
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <span className="opacity-70">Field Completion</span>
                <div className="font-semibold">{Math.round(confidenceBreakdown.field_completion || 0)}%</div>
              </div>
              <div>
                <span className="opacity-70">Data Quality</span>
                <div className="font-semibold">{Math.round(confidenceBreakdown.data_quality || 0)}%</div>
              </div>
              <div>
                <span className="opacity-70">Product Match</span>
                <div className="font-semibold">{Math.round(confidenceBreakdown.product_match || 0)}%</div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Invoice Header */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <label className="text-sm text-gray-500 flex items-center">
              <Building className="w-4 h-4 mr-1" /> Vendor
            </label>
            <input
              type="text"
              value={editedData.vendor_name || ''}
              onChange={(e) => setEditedData({ ...editedData, vendor_name: e.target.value })}
              className="w-full mt-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
              placeholder="Vendor name"
            />
          </div>
          
          <div>
            <label className="text-sm text-gray-500 flex items-center">
              <FileText className="w-4 h-4 mr-1" /> Invoice #
            </label>
            <input
              type="text"
              value={editedData.invoice_number || ''}
              onChange={(e) => setEditedData({ ...editedData, invoice_number: e.target.value })}
              className="w-full mt-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
              placeholder="Invoice number"
            />
          </div>
          
          <div>
            <label className="text-sm text-gray-500 flex items-center">
              <Calendar className="w-4 h-4 mr-1" /> Date
            </label>
            <input
              type="date"
              value={editedData.invoice_date || ''}
              onChange={(e) => setEditedData({ ...editedData, invoice_date: e.target.value })}
              className="w-full mt-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
            />
          </div>
          
          <div>
            <label className="text-sm text-gray-500 flex items-center">
              <DollarSign className="w-4 h-4 mr-1" /> Total
            </label>
            <input
              type="number"
              step="0.01"
              value={editedData.total_amount || ''}
              onChange={(e) => setEditedData({ ...editedData, total_amount: parseFloat(e.target.value) })}
              className="w-full mt-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
            />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="border-b">
          <div className="flex">
            <button
              onClick={() => setActiveTab('items')}
              className={`px-6 py-3 font-medium ${activeTab === 'items' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500'}`}
            >
              <Package className="w-4 h-4 inline mr-2" />
              Line Items ({editedData.line_items?.length || 0})
            </button>
            <button
              onClick={() => setActiveTab('summary')}
              className={`px-6 py-3 font-medium ${activeTab === 'summary' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500'}`}
            >
              <BarChart3 className="w-4 h-4 inline mr-2" />
              Summary
            </button>
          </div>
        </div>

        {/* Line Items Tab */}
        {activeTab === 'items' && (
          <div className="p-6">
            {/* Search and Filter Bar */}
            <div className="flex flex-col md:flex-row gap-4 mb-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search items..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                />
              </div>
              
              <div className="flex gap-2">
                <select
                  value={filterMatched}
                  onChange={(e) => setFilterMatched(e.target.value)}
                  className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Items</option>
                  <option value="matched">Matched Products</option>
                  <option value="new">New Products</option>
                </select>
              </div>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 w-10">
                      <input
                        type="checkbox"
                        checked={selectAll}
                        onChange={(e) => {
                          setSelectAll(e.target.checked);
                          setSelectedItems(e.target.checked ? editedData.line_items.map((_, i) => i) : []);
                        }}
                        className="rounded"
                      />
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">#</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Item</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Qty</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Unit</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Cost</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Total</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">UPC</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Match</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                
                <tbody className="divide-y">
                  {editedData.line_items
                    ?.filter((item, index) => {
                      const matchesSearch = item.item_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                          item.upc?.includes(searchTerm);
                      const matchesFilter = filterMatched === 'all' ||
                                          (filterMatched === 'matched' && item.matched_product_id) ||
                                          (filterMatched === 'new' && !item.matched_product_id);
                      return matchesSearch && matchesFilter;
                    })
                    ?.map((item, index) => (
                    <tr key={index} className={item.matched_product_id ? 'bg-green-50' : 'hover:bg-gray-50'}>
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selectedItems.includes(index)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedItems([...selectedItems, index]);
                            } else {
                              setSelectedItems(selectedItems.filter(i => i !== index));
                            }
                          }}
                          className="rounded"
                        />
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">{index + 1}</td>
                      <td className="px-4 py-3">
                        <input
                          type="text"
                          value={item.item_name}
                          onChange={(e) => updateLineItem(index, 'item_name', e.target.value)}
                          className="w-full px-2 py-1 border rounded focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                        />
                      </td>
                      
                      <td className="px-4 py-3">
                        <input
                          type="number"
                          value={item.quantity}
                          onChange={(e) => updateLineItem(index, 'quantity', parseFloat(e.target.value))}
                          className="w-20 px-2 py-1 border rounded focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                        />
                      </td>
                      
                      <td className="px-4 py-3">
                        <select
                          value={item.unit}
                          onChange={(e) => updateLineItem(index, 'unit', e.target.value)}
                          className="px-2 py-1 border rounded focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                        >
                          <option value="each">Each</option>
                          <option value="case">Case</option>
                          <option value="bottle">Bottle</option>
                          <option value="pack">Pack</option>
                          <option value="lb">Lb</option>
                          <option value="oz">Oz</option>
                        </select>
                      </td>
                      
                      <td className="px-4 py-3">
                        <input
                          type="number"
                          step="0.01"
                          value={item.unit_cost}
                          onChange={(e) => updateLineItem(index, 'unit_cost', parseFloat(e.target.value))}
                          className="w-24 px-2 py-1 border rounded focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                        />
                      </td>
                      
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">
                        ${((item.quantity || 0) * (item.unit_cost || 0)).toFixed(2)}
                      </td>
                      
                      <td className="px-4 py-3">
                        <input
                          type="text"
                          value={item.upc || ''}
                          onChange={(e) => updateLineItem(index, 'upc', e.target.value)}
                          className="w-32 px-2 py-1 border rounded focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                          placeholder="UPC"
                        />
                      </td>
                      
                      <td className="px-4 py-3">
                        {item.matched_product_id ? (
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 text-green-800`}>
                            <Check className="w-3 h-3 mr-1" />
                            {item.match_method === 'upc' ? 'UPC Match' : 
                             item.match_method === 'sku' ? 'SKU Match' : 
                             item.match_method === 'fuzzy_name' ? 'Name Match' : 'Matched'}
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-600">
                            New Product
                          </span>
                        )}
                      </td>
                      
                      <td className="px-4 py-3">
                        <button
                          onClick={() => removeLineItem(index)}
                          className="text-red-500 hover:text-red-700"
                          title="Remove item"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {selectedItems.length > 0 && (
              <div className="mt-4 p-3 bg-blue-50 rounded-lg flex items-center justify-between">
                <span className="text-sm text-blue-800">
                  {selectedItems.length} item(s) selected
                </span>
                <button
                  onClick={() => {
                    const newItems = editedData.line_items.filter((_, i) => !selectedItems.includes(i));
                    setEditedData({ ...editedData, line_items: newItems });
                    setSelectedItems([]);
                    setSelectAll(false);
                    toast.success(`${selectedItems.length} items removed`);
                  }}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm"
                >
                  Remove Selected
                </button>
              </div>
            )}
            
            <button
              onClick={addLineItem}
              className="mt-4 px-4 py-2 border border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-500 hover:text-blue-600 w-full"
            >
              + Add Line Item
            </button>
          </div>
        )}

        {/* Summary Tab */}
        {activeTab === 'summary' && (
          <div className="p-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className="bg-blue-50 rounded-lg p-4">
                <div className="text-sm text-blue-600 mb-1">Total Items</div>
                <div className="text-2xl font-bold text-blue-900">{totals.itemCount}</div>
              </div>
              <div className="bg-green-50 rounded-lg p-4">
                <div className="text-sm text-green-600 mb-1">Subtotal</div>
                <div className="text-2xl font-bold text-green-900">${totals.subtotal.toFixed(2)}</div>
              </div>
              <div className="bg-yellow-50 rounded-lg p-4">
                <div className="text-sm text-yellow-600 mb-1">Tax</div>
                <div className="text-2xl font-bold text-yellow-900">${totals.tax.toFixed(2)}</div>
              </div>
              <div className="bg-purple-50 rounded-lg p-4">
                <div className="text-sm text-purple-600 mb-1">Total</div>
                <div className="text-2xl font-bold text-purple-900">${totals.total.toFixed(2)}</div>
              </div>
            </div>
            
            {editedData.notes && (
              <div className="mt-6">
                <h4 className="font-medium mb-2">Notes</h4>
                <p className="text-gray-600 bg-gray-50 p-3 rounded">{editedData.notes}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-4">
        <button
          onClick={handleReject}
          disabled={rejecting || approving}
          className="px-6 py-3 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 disabled:opacity-50"
        >
          {rejecting ? (
            <><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Rejecting...</>
          ) : (
            <><X className="w-4 h-4 inline mr-2" />Reject</>
          )}
        </button>
        
        <button
          onClick={handleApprove}
          disabled={approving || rejecting}
          className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          {approving ? (
            <><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Processing...</>
          ) : (
            <><Check className="w-4 h-4 inline mr-2" />Approve & Update Inventory</>
          )}
        </button>
      </div>
    </div>
  );
}
